import { http } from '@/api/http';
import { isAdminLocalModeEnabled, isPublicMockEnabled } from '@/config/featureFlags';
import { withBoardReadFallback } from '@/features/board/requestFallback';
import {
  createMockCertificationAsset,
  deleteMockCertificationAsset,
  fetchMockCertificationAssets,
  fetchMockAdminCertificationAsset,
  fetchMockAdminCertificationAssets,
  updateMockCertificationAsset,
} from '@/features/content/mockData';
import type {
  AdminCertificationAssetDetail,
  AdminCertificationAssetSummary,
  CertificationAssetMutationInput,
  CertificationAssetResponse,
} from '@/features/content/types';

const appendIfPresent = (formData: FormData, key: string, value: File | null) => {
  if (value) {
    formData.append(key, value);
  }
};

const createCertificationAssetFormData = (input: CertificationAssetMutationInput) => {
  const formData = new FormData();
  formData.append('category', input.category);
  formData.append('title', input.title);
  formData.append('acquiredYear', String(input.acquiredYear));
  formData.append('imageFit', input.imageFit);
  formData.append('imageObjectPosition', input.imageObjectPosition);
  formData.append('imageScale', String(input.imageScale));
  formData.append('displayOrder', String(input.displayOrder));
  formData.append('isPublished', String(input.isPublished));
  formData.append('publishedAt', input.publishedAt);
  formData.append('translationsJson', JSON.stringify(input.translations ?? []));
  appendIfPresent(formData, 'image', input.image);
  return formData;
};

const shouldUsePublicContentMocks = isPublicMockEnabled();
const shouldUseAdminContentLocalMode = isAdminLocalModeEnabled();

export const fetchCertificationAssets = async (): Promise<CertificationAssetResponse[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get('/api/v1/certification-assets'),
    fallbackRequest: fetchMockCertificationAssets,
    label: 'public certification assets',
    useMock: shouldUsePublicContentMocks,
  });
};

export const fetchAdminCertificationAssets = async (): Promise<
  AdminCertificationAssetSummary[]
> => {
  return withBoardReadFallback({
    apiRequest: () => http.get('/api/v1/admin/certification-assets'),
    fallbackRequest: fetchMockAdminCertificationAssets,
    label: 'admin certification assets',
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const fetchAdminCertificationAsset = async (
  certificationAssetId: number,
): Promise<AdminCertificationAssetDetail> => {
  return withBoardReadFallback({
    apiRequest: () =>
      http.get(`/api/v1/admin/certification-assets/${String(certificationAssetId)}`),
    fallbackRequest: () => fetchMockAdminCertificationAsset(certificationAssetId),
    label: `admin certification asset:${String(certificationAssetId)}`,
    useMock: shouldUseAdminContentLocalMode,
  });
};

export const createCertificationAsset = async (
  input: CertificationAssetMutationInput,
): Promise<AdminCertificationAssetDetail> => {
  if (shouldUseAdminContentLocalMode) {
    return createMockCertificationAsset(input);
  }

  return http.post('/api/v1/admin/certification-assets', createCertificationAssetFormData(input));
};

export const updateCertificationAsset = async (
  certificationAssetId: number,
  input: CertificationAssetMutationInput,
): Promise<AdminCertificationAssetDetail> => {
  if (shouldUseAdminContentLocalMode) {
    return updateMockCertificationAsset(certificationAssetId, input);
  }

  return http.put(
    `/api/v1/admin/certification-assets/${String(certificationAssetId)}`,
    createCertificationAssetFormData(input),
  );
};

export const deleteCertificationAsset = async (certificationAssetId: number): Promise<void> => {
  if (shouldUseAdminContentLocalMode) {
    return deleteMockCertificationAsset(certificationAssetId);
  }

  return http.delete(`/api/v1/admin/certification-assets/${String(certificationAssetId)}`);
};
